import { useFormatDate } from "@/elements/hooks/use-format-date";
import React, { Fragment, useState } from "react";
import { Text, View } from "react-native";
import CheckIcon from "@/assets/svgs/check-icon";
import ClockIcon from "@/assets/svgs/clock-icon";
import CloseIcon from "@/assets/svgs/close-icon";
import RemoteIcon from "@/assets/svgs/remote-icon";
import UserIcon from "@/assets/svgs/user-icon";
import RequestRejectedModal from "../modals/request-rejected-modal";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import type { api } from "../utils/convex-api";

function TutorBookingCard({
  booking,
  onReview,
  onDecline,
}: {
  booking: NonNullable<typeof api.tutor.home.queries.getPendingBookings._returnType>[0];
  onReview: () => void;
  onDecline: (reason?: string) => void;
}) {
  const fmt = useFormatDate();
  const [rejectModalVisible, setRejectModalVisible] = useState(false);

  return (
    <Fragment>
      <View
        key={booking.bookingId}
        className="border border-gray-200 p-4 rounded-[17px] gap-4"
      >
        <View className="flex-row justify-between">
          <View className="flex-row items-start gap-2">
            <Avatar size="sm" className="h-8 w-8">
              <Avatar.Content
                label={booking.student.name ?? ""}
                labelSize="sm"
                size="sm"
              />
            </Avatar>
            <View className="flex-1">
              <Text className="font-geologica-medium font-medium text-base text-gray-800 leading-[1.5]">
                {booking.student.name}
              </Text>
              <Text className="font-geologica-regular font-normal text-sm text-gray-800 leading-[1.5]">
                Parent: {booking.parentName}
              </Text>
            </View>
          </View>
          <Text className="font-geologica-medium font-medium text-base text-gray-800 leading-[1.2]">
            {fmt(booking.startTime, "do MMMM")}
          </Text>
        </View>

        <View className="flex-row items-center justify-between">
          <Text className="font-geologica-regular font-normal text-gray-800 text-sm leading-[1.5]">
            {booking.subjectName}
          </Text>

          <View className="flex-row items-center gap-[6px]">
            {booking.deliveryMethod === "in_person" ? (
              <RemoteIcon
                className="size-4"
                pathClassName={"stroke-gray-800"}
              />
            ) : (
              <UserIcon className="size-4" pathClassName={"stroke-gray-800"} />
            )}
            <Text className="font-geologica-regular font-normal text-gray-800 text-sm leading-[1.5]">
              {booking.deliveryMethod === "in_person" ? "In person" : "Remote"}
            </Text>
          </View>

          <View className="flex-row items-center gap-2">
            <ClockIcon className="size-4" pathClassName={"stroke-gray-800"} />
            <Text className="font-geologica-regular font-normal text-gray-800 text-sm leading-[1.5]">
              {fmt(booking.startTime, "HH:mm")} -{" "}
              {fmt(booking.endTime, "HH:mm")}
            </Text>
          </View>
        </View>

        {!!booking.notes && (
          <Text className="font-geologica-regular font-normal text-gray-800 text-sm leading-[1.5]">
            "{booking.notes}"
          </Text>
        )}

        <View className="flex-row items-center justify-end gap-2">
          <Button
            intent="danger"
            size="sm"
            outline
            icon
            className="border"
            onPress={() => setRejectModalVisible(true)}
          >
            <Button.Icon as={CloseIcon} pathClassName="stroke-red-500" />
            <Button.Text intent="danger" size="sm">
              Decline
            </Button.Text>
          </Button>
          <Button
            intent="primary"
            size="sm"
            icon
            className="border"
            onPress={onReview}
          >
            <Button.Icon as={CheckIcon} pathClassName="stroke-white" />
            <Button.Text size="sm">Review</Button.Text>
          </Button>
        </View>
      </View>

      <RequestRejectedModal
        isVisible={rejectModalVisible}
        onClose={() => setRejectModalVisible(false)}
        onDismiss={onDecline}
      />
    </Fragment>
  );
}

export default TutorBookingCard;
